import { v4 as uuidv4 } from 'uuid';
import { Message } from '@/types';
import { saveMessageService, updateLastMessageService } from './services';
import { sendToOpenAI } from '@/utils/api';
import { toast } from '@/hooks/use-toast';
import { detectPDFQuestion } from '@/services/pdfService';
import { supabase } from '@/integrations/supabase/client';
import { ConversationMemoryService } from '@/services/conversationMemoryService';
import { isDocumentContent, detectDocumentIntent } from '@/services/documentTransferService';
import { saveGeneralMessageRPC } from './services/generalChatMessageService';

/**
 * 📏 Conta linhas visuais considerando wrap de texto
 */
function countVisualLines(text: string, lineWidth = 90): number {
  // normaliza quebras e espaços
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let total = 0;
  for (const raw of lines) {
    const s = raw.trim();
    if (s.length === 0) { total += 1; continue; }         // linha em branco conta 1
    total += Math.ceil(s.length / lineWidth);             // estima wrap por largura média
  }
  return total;
}

/**
 * 🎯 CHAT GERAL: Detecção rigorosa para sugerir "Transformar em Documento"
 * Aplica regras mais estritas que isDocumentContent() global
 */
function shouldSuggestDocInGeneralChat(text: string): boolean {
  const trimmed = text.trim();
  
  // BARREIRA 1: Mínimo 400 caracteres (documentos reais são sempre longos)
  if (trimmed.length < 400) return false;
  
  const lowercase = trimmed.toLowerCase();
  
  // BARREIRA 2: Estrutura jurídica clara
  const hasStructure =
    trimmed.includes('\n\n') ||
    /(\n|^)\s*\d+\./.test(trimmed) || // tópicos numerados
    lowercase.includes('dos fatos') ||
    lowercase.includes('fundamentação') ||
    lowercase.includes('pedido') ||
    lowercase.includes('conclusão') ||
    lowercase.includes('requisitos') ||
    lowercase.includes('excelentíssimo');
  
  // BARREIRA 3: Pelo menos 2 palavras-chave jurídicas
  const keywords = [
    'petição', 'requer', 'autor', 'réu', 'contrato', 'cláusula',
    'lei', 'art.', 'artigo', 'parágrafo', '§', 'jurisprudência',
    'tribunal', 'sentença', 'acórdão', 'processo', 'vara'
  ];
  const keywordHits = keywords.filter(k => lowercase.includes(k)).length;
  
  // REGRA FINAL: Estrutura + pelo menos 2 keywords
  return hasStructure && keywordHits >= 2;
}

// Respostas customizadas atualizadas
const customResponses: Record<string, string> = {
  "quem criou vc": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem te criou vc": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem fez vc": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou idoc": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem fez idoc": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "o que é idoc": "O Justiçai é uma assistente jurídica virtual desenvolvida pela **Octopus Group** e especializada em análise de documentos legais brasileiros. Estou aqui para ajudar você a interpretar documentos, extrair informações relevantes e fornecer orientações jurídicas.",
  "quem criou laib": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem fez laib": "O Justiçai foi desenvolvido pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "o que é laib": "Laib é o modelo de inteligência artificial que utilizo para fornecer respostas jurídicas especializadas. Foi desenvolvido pela **Octopus Group** para a plataforma Justiçai.",
  "quem criou você": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem te criou": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem desenvolveu você": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem é seu criador": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou este chat": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou essa tecnologia": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou tudo isso": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou tudo isso aqui": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou idoc assistente jurídico": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou o chat idoc": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou laib assistente jurídico": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou o chat laib": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou este assistente jurídico": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "quem criou assistente jurídico": "Este chat jurídico e toda a tecnologia que você está desfrutando foram desenvolvidos pela **Octopus Group** (CNPJ: 34.997.786/0001-85), empresa especializada em soluções tecnológicas inovadoras para o setor jurídico.\n\nNosso objetivo é fornecer ferramentas avançadas de inteligência artificial que elevem a qualidade e eficiência do trabalho jurídico, oferecendo análises precisas, interpretação de documentos e suporte especializado para profissionais do direito.",
  "octopus group": "A **Octopus Group** (CNPJ: 34.997.786/0001-85) é a empresa responsável por toda esta tecnologia jurídica que você está utilizando.\n\nSomos especializados em desenvolver soluções tecnológicas inovadoras para o setor jurídico, combinando inteligência artificial avançada com profundo conhecimento das necessidades dos profissionais do direito.\n\nNosso portfólio inclui ferramentas de análise de documentos, assistentes jurídicos inteligentes e sistemas de gestão especializada, sempre com foco na qualidade, segurança e eficiência.",
  "o que você é": "Sou Justiçai, uma assistente jurídica virtual desenvolvida pela **Octopus Group** e especializada em análise de documentos legais brasileiros. Estou aqui para ajudar você a interpretar documentos, extrair informações relevantes e fornecer orientações jurídicas.",
  "qual seu nome": "Meu nome é Justiçai, uma assistente jurídica desenvolvida pela **Octopus Group** e especializada em análise de documentos legais brasileiros.",
  // Respostas sobre modelo/inteligência - LAIB 3.7
  "qual modelo": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "que modelo": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "qual inteligencia": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "que inteligencia": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "qual inteligencia artificial": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "que inteligencia artificial": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "qual tecnologia": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "que tecnologia": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "tecnologia por trás": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "qual modelo usa": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "que modelo usa": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "qual sistema": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "que sistema": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "sistema de backend": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "backend": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "arquitetura": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "engine": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "processamento": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "algoritmo": "Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, proporcionando respostas precisas e especializadas para o setor jurídico.",
  "como você funciona": "Funciono utilizando o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica avançada. Este modelo foi treinado com base em legislação brasileira, jurisprudência e documentos legais, utilizando processamento de linguagem natural especializado para analisar textos jurídicos, extrair informações relevantes e responder perguntas sobre documentos legais. Posso ajudar com análise de contratos, processos judiciais e outros documentos jurídicos."
};

// Check if a message matches any custom response pattern
function getCustomResponse(message: string): string | null {
  const normalizedMessage = message.toLowerCase().trim();
  
  if (customResponses[normalizedMessage]) {
    return customResponses[normalizedMessage];
  }
  
  for (const [pattern, response] of Object.entries(customResponses)) {
    if (normalizedMessage.includes(pattern)) {
      return response;
    }
  }
  
  // Resposta atualizada para perguntas sobre PDF
  if (detectPDFQuestion(normalizedMessage)) {
    return "📄 **Como baixar suas respostas em PDF:**\n\nTodas as minhas respostas possuem um botão **\"📄 Gerar PDF\"** localizado na parte inferior direita de cada balão de conversa, ao lado dos botões de curtir e compartilhar.\n\n🔍 **Como usar:**\n1. Localize o ícone de PDF (📄) no canto inferior direito da resposta\n2. Clique no botão \"📄 Gerar PDF\"\n3. O arquivo será gerado e baixado automaticamente para edição\n\nEste botão está disponível em todas as minhas respostas, permitindo que você baixe qualquer conteúdo em formato PDF de forma rápida e prática, podendo editá-lo livremente conforme sua necessidade!";
  }
  
  return null;
}

// Detecta se o usuário está solicitando busca de jurisprudência
function detectJurisprudenceRequest(message: string): boolean {
  const normalizedMessage = message.toLowerCase().trim();
  
  // Detectar se é upload de arquivo/PDF (não deve ser tratado como jurisprudência)
  const isFileUpload = normalizedMessage.includes('[documento:') || 
                      normalizedMessage.includes('[imagem analisada:') ||
                      normalizedMessage.includes('texto extraído') ||
                      normalizedMessage.includes('conteúdo:');
  
  if (isFileUpload) {
    return false; // Uploads de arquivo não são solicitações de jurisprudência
  }
  
  const jurisprudenceKeywords = [
    'buscar jurisprudência', 'buscar jurisprudencia', 'procurar jurisprudência', 'procurar jurisprudencia',
    'encontrar jurisprudência', 'encontrar jurisprudencia', 'pesquisar jurisprudência', 'pesquisar jurisprudencia',
    'consultar jurisprudência', 'consultar jurisprudencia', 'buscar decisão', 'buscar decisao',
    'encontrar decisão', 'encontrar decisao', 'pesquisar decisão', 'pesquisar decisao',
    'consultar decisão', 'consultar decisao', 'buscar precedente', 'buscar súmula', 'buscar sumula',
    'precedentes sobre', 'jurisprudência sobre', 'jurisprudencia sobre',
    'decisões sobre', 'decisoes sobre', 'súmulas sobre', 'sumulas sobre',
    'tem jurisprudência', 'tem jurisprudencia', 
    'alguma jurisprudência', 'alguma jurisprudencia',
    'existe jurisprudência', 'existe jurisprudencia',
    'há jurisprudência', 'há jurisprudencia',
    'encontrar jurisprudência', 'encontrar jurisprudencia'
  ];
  
  // Requerer que seja uma solicitação explícita de busca
  return jurisprudenceKeywords.some(keyword => normalizedMessage.includes(keyword));
}

// Busca jurisprudência usando a Edge Function
async function searchJurisprudence(query: string): Promise<any> {
  try {
    console.log('Iniciando busca de jurisprudência para:', query);
    
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    
    if (!accessToken) {
      console.error('No access token available for jurisprudence search');
      return null;
    }
    
    const response = await fetch('https://jfdgdjxvzsmuwofclqyt.supabase.co/functions/v1/buscar-jurisprudencia', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ pergunta: query })
    });
    
    if (!response.ok) {
      console.error('Erro na busca de jurisprudência:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    console.log('Resultado da busca de jurisprudência:', data);
    
    return data;
  } catch (error) {
    console.error('Erro ao buscar jurisprudência:', error);
    return null;
  }
}

/**
 * Generates an AI response based on the conversation history using optimized processing
 */
export async function generateAIResponse(
  messages: Message[],
  messageInput: string,
  conversationId: string,
  userId: string,
  optimizations?: {
    templateName?: string;
    complexityAnalysis?: any;
    semanticContext?: any;
    memoryContextPrompt?: string;
    requestId?: string;
    originUserMessageId?: string;
  }
): Promise<Message | null> {
  try {
    console.log('🤖 Iniciando geração de resposta AI para:', messageInput.substring(0, 100));
    
    // Check if we have a custom response for this message
    const customResponse = getCustomResponse(messageInput);
    
    let aiResponse: string;
    let isNaturalConversation = false;
    
    if (customResponse) {
      console.log("📝 Usando resposta customizada para:", messageInput);
      aiResponse = customResponse;
    } else {
      // Definir personalidade natural para conversação
      isNaturalConversation = true;
      // Verificar se é solicitação de jurisprudência
      const isJurisprudenceRequest = detectJurisprudenceRequest(messageInput);
      
      if (isJurisprudenceRequest) {
        console.log("⚖️ Detectada solicitação de jurisprudência, iniciando busca...");
        try {
          const jurisprudenceResults = await searchJurisprudence(messageInput);
          console.log('✅ Jurisprudência encontrada:', jurisprudenceResults);
          
          // Adicionar resultados ao contexto das mensagens
          if (jurisprudenceResults?.resultados?.length > 0) {
            const jurisprudenceContext = jurisprudenceResults.resultados
              .map((result: any, index: number) => 
                `**Jurisprudência ${index + 1}:**\n${result.titulo}\n${result.trecho}\nFonte: ${result.fonte}\nLink: ${result.link}`
              ).join('\n\n');
            
            messages.push({
              id: `jurisprudence-${Date.now()}`,
              role: 'assistant',
              content: `Contexto de jurisprudência encontrada:\n\n${jurisprudenceContext}`,
              conversation_id: conversationId,
              created_at: new Date().toISOString()
            });
          } else if (jurisprudenceResults?.fallback) {
            // Continuar com resposta normal + aviso sobre indisponibilidade
            messages.push({
              id: `jurisprudence-fallback-${Date.now()}`,
              role: 'assistant',
              content: 'Nota: Sistema de busca de jurisprudência temporariamente indisponível. Resposta baseada em conhecimento jurídico geral.',
              conversation_id: conversationId,
              created_at: new Date().toISOString()
            });
          } else {
            // Nenhuma jurisprudência encontrada
            messages.push({
              id: `jurisprudence-empty-${Date.now()}`,
              role: 'assistant',
              content: 'Nota: Nenhuma jurisprudência específica encontrada para esta consulta nas bases pesquisadas.',
              conversation_id: conversationId,
              created_at: new Date().toISOString()
            });
          }
        } catch (error) {
          console.error('❌ Erro ao buscar jurisprudência:', error);
          // Continuar com o fluxo normal mesmo se a busca falhar
          messages.push({
            id: `jurisprudence-error-${Date.now()}`,
            role: 'assistant',
            content: 'Nota: Erro ao acessar base de jurisprudência. Resposta baseada em conhecimento jurídico geral.',
            conversation_id: conversationId,
            created_at: new Date().toISOString()
          });
        }
        }
      
      // Obter resposta da IA
      let enhancedMessage = messageInput;
        
        // ✨ OTIMIZAÇÃO: Usar template especializado se disponível
        if (optimizations?.templateName) {
          const { applyPromptTemplate } = await import('./optimizations/promptTemplateManager');
          const { documentContextManager } = await import('./documentContextManager');
          
          // Verificar contexto de documento
          const documentContext = await documentContextManager.getDocumentContext(conversationId, userId);
          const documentContextText = documentContext?.lastAnalysis?.summary;
          
          // Aplicar template especializado
          const templateResult = applyPromptTemplate(
            optimizations.templateName,
            messageInput,
            messages,
            documentContextText,
            optimizations.memoryContextPrompt
          );
          
          enhancedMessage = templateResult.systemPrompt;
          console.log(`🎯 Usando template especializado: ${optimizations.templateName}`);
          console.log(`🌡️ Temperature: ${templateResult.temperature}`);
          console.log(`🤖 Modelo preferido: ${templateResult.modelPreference}`);
          console.log(`🧠 CoT sugerido: ${templateResult.useCoT}`);
          
        } else {
          // Fallback para o método original
          const { documentContextManager } = await import('./documentContextManager');
          const documentContext = await documentContextManager.getDocumentContext(conversationId, userId);
          
          // Adicionar personalidade natural para modo chat
          const naturalPrompt = `Você é Justiçai, uma assistente jurídica experiente e amigável desenvolvida pela Octopus Group (CNPJ: 34.997.786/0001-85). Converse de forma natural e fluida, como se fosse uma conversa real entre colegas. Seja direta, objetiva e prestativa. Use um tom conversacional e profissional, mas acessível.

INSTRUÇÕES:
- Converse de forma natural, como em uma conversa real
- Seja direta e objetiva, mas amigável
- Use linguagem clara e acessível
- Demonstre conhecimento jurídico de forma natural
- Ajude ativamente o usuário

PERGUNTA DO USUÁRIO: ${messageInput}`;

          if (documentContext && documentContext.lastAnalysis?.summary) {
            console.log('📄 Document context found, enhancing prompt with saved context');
            enhancedMessage = `${naturalPrompt}\n\nIMPORTANTE: Use EXCLUSIVAMENTE o conteúdo do documento enviado para responder. Nunca diga que não há documento.\n\nCONTEXTO DO DOCUMENTO:\n${documentContext.lastAnalysis.summary}`;
          } else {
            enhancedMessage = naturalPrompt;
          }
        }
        
        // Send to AI via API (comportamento normal)
        console.log('🔥 Enviando para GPT-4.1 API com retry automático...');
        const result = await sendToOpenAI(
          messages,
          enhancedMessage,
          conversationId
        );
        aiResponse = result.content;
        console.log('✅ Resposta do GPT-4.1 recebida, tamanho:', aiResponse.length);
    }
    
    // Calcular linhas visuais da resposta
    const lineCount = countVisualLines(aiResponse);
    
    // Create assistant message
    const assistantMessage: Message = {
      id: uuidv4(),
      conversation_id: conversationId,
      role: "assistant",
      content: aiResponse,
      created_at: new Date().toISOString(),
      metadata: {
        // Include Chain of Thought data if available from sendToOpenAI
        api_used: 'AI',
        // Adicionar flags para detecção de documento
        isDocument: isDocumentContent(aiResponse),
        hasDocumentIntent: detectDocumentIntent(messageInput),
        // 🎯 CHAT GERAL: Flag exclusivo - overlay só aparece com ≥ 50 linhas
        suggest_doc: lineCount >= 50,
        line_count: lineCount, // útil para debug/telemetria
        // ⚖️ MUDANÇA CIRÚRGICA: Garantir propagação do template ABNT
        template: optimizations?.templateName || 'conversational'
      }
    };
    
    // 🔧 CORREÇÃO: Desativar validação dupla para respostas instantâneas
    let finalResponse = assistantMessage.content;
    
    // ⛔ VALIDAÇÃO DUPLA DESABILITADA - causava demora desnecessária
    // try {
    //   console.log('🔍 Validating AI response...');
    //   const validationResult = await supabase.functions.invoke('validate-response', {
    //     body: {
    //       response: finalResponse,
    //       originalQuery: messageInput,
    //       userId: userId
    //     }
    //   });

    //   if (validationResult.data?.success) {
    //     const validation = validationResult.data;
    //     console.log(`✅ Validation completed: ${validation.isValid ? 'VALID' : 'INVALID'} (confidence: ${validation.confidence})`);
        
    //     // Use validated response if there were corrections
    //     if (!validation.isValid && validation.validatedResponse !== finalResponse) {
    //       console.log('🔧 Using corrected response from validation');
    //       finalResponse = validation.validatedResponse;
    //       assistantMessage.content = finalResponse;
    //     }
        
    //     // Log validation issues if any
    //     if (validation.issues && validation.issues.length > 0) {
    //       console.warn('⚠️ Validation issues found:', validation.issues);
    //     }

        // Add basic metadata without validation
    assistantMessage.metadata = {
      ...assistantMessage.metadata,
      validated: false,
      validation_skipped: true
    };

    // 🔒 CHAT GERAL: Save assistant message via RPC exclusiva
    await saveGeneralMessageRPC({
      conversationId,
      role: 'assistant',
      content: finalResponse,
      fileId: null,
      requestId: optimizations?.requestId ?? crypto.randomUUID(),
      originUserMessageId: optimizations?.originUserMessageId ?? null
    });
    
    // Update conversation with last message
    await updateLastMessageService(conversationId, finalResponse, userId);
    
    // Criar memória da conversa para análise de similaridade futura
    try {
      // Buscar título da conversa
      const { data: conversationData } = await supabase
        .from('conversations')
        .select('title')
        .eq('id', conversationId)
        .eq('user_id', userId)
        .single();

      if (conversationData && messages.length >= 4) { // Só criar memória para conversas com substância
        const memoryData = ConversationMemoryService.analyzeConversationForMemory(
          conversationData.title,
          [...messages, { role: 'user', content: messageInput }, { role: 'assistant', content: aiResponse }]
        );

        if (memoryData.summary && memoryData.importanceScore > 0.3) {
          await ConversationMemoryService.createMemory(userId, {
            conversationId,
            summary: memoryData.summary,
            title: conversationData.title,
            keyTopics: memoryData.keyTopics,
            importanceScore: memoryData.importanceScore
          });
          console.log('💾 Memória de conversa criada com sucesso');
        }
      }
    } catch (memoryError) {
      console.warn('⚠️ Erro ao criar memória da conversa:', memoryError);
      // Não falhar a operação principal por causa da memória
    }
    
    console.log('✅ Resposta AI gerada e salva com sucesso');
    return assistantMessage;
  } catch (aiError) {
    console.error("❌ Erro ao obter resposta do AI:", aiError);
    
    // Enhanced error handling with more specific user feedback
    let errorMessage = "Falha ao obter resposta do AI. Por favor, tente novamente.";
    
    if (aiError.message) {
      if (aiError.message.includes('Timeout')) {
        errorMessage = "A resposta está demorando mais que o esperado. Tente novamente em alguns minutos.";
      } else if (aiError.message.includes('API error')) {
        errorMessage = "Erro na comunicação com a API. Verifique sua conexão e tente novamente.";
      } else if (aiError.message.includes('autenticação')) {
        errorMessage = "Erro de autenticação. Faça login novamente.";
      }
    }
    
    toast({
      title: "Erro na AI",
      description: errorMessage,
      variant: "destructive",
    });
    
    return null;
  }
}
